import React, { useCallback, useContext, useMemo, useState } from 'react'
import { EditorState } from 'lexical'
import classNames from 'classnames'
import { nanoid } from 'nanoid'

import { RichTextToolbarPlugin } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { WritableLexicalEditor } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { ReadOnlyLexicalEditor } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/ReadOnlyLexicalEditor/ReadOnlyLexicalEditor'
import { EditablePlugin } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/EditablePlugin/EditablePlugin'
import { getEditorHtmlContent, getEditorStateFromHtml } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'
import { LEXICAL_NODES } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { selectDocument, selectDocumentFileInfo } from '../../../store/processDocument/document'
import { useInteractWithAiAgentMutation } from '../../../store/ai/aiAgent'
import { addMessage } from '../../../store/streamingChat/streamingChat'
import { openModal } from '../../../store/modal/modal'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import {
  selectDocumentSummaryAiAgentId,
  selectDocumentSummaryEditing,
  selectDocumentSummaryExecutionLoading,
  selectDocumentSummaryInteractionLoading,
  selectDocumentSummaryLoading,
  selectDocumentSummaryPollingEnabled,
  selectDocumentSummaryResult,
  setDocumentSummaryAiAgentId,
  setDocumentSummaryEditing,
  setDocumentSummaryInteractionLoading,
  setDocumentSummaryResult,
} from '../../../store/processDocument/documentSummary'

import { CustomDropdownItemOption, DropDownItemOptionComponentProps, SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { DocumentConnectorActionButton, type DocumentConnectorActionItem } from '../../../components/DocumentConnector/DocumentConnectorActionButton'
import { IntegrationExportButton } from '../../../components/IntegrationExportButton/IntegrationExportButton'
import { Button } from '../../../components/Button/Button'

import { getIntegrationDisplayName, IntegrationServiceId } from '../../../common/integrations'
import { useCurrentUserIntegrations } from '../../Integration/useCurrentUserIntegrations'
import { useLastUsedDocumentConnector } from '../../../common/lastUsedDocumentConnector'

import { DocumentSummaryInteractionResultType } from '../../../models/ProcessDocument'

import { useDocumentSummaryAiAgentResult } from '../hooks'

import { useDocumentSummary } from './hooks'
import { DocumentSummaryContentRefContext } from './DocumentSummaryContentRefContext'

const getEditorHeaderDropdownOptions = (editingDisabled: boolean = false, rerunDisabled: boolean = false) => [
  {
    icon: 'bi-pencil-square',
    id: 'edit',
    label: 'Edit',
    disabled: editingDisabled,
  },
  {
    icon: 'bi-arrow-clockwise',
    id: 'rerun',
    label: 'Rerun',
    disabled: rerunDisabled,
  },
]

export const DocumentSummaryEditor = () => {
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)
  const documentSummaryResult = useAppSelector(selectDocumentSummaryResult)
  const aiAgentId = useAppSelector(selectDocumentSummaryAiAgentId)
  const summaryLoading = useAppSelector(selectDocumentSummaryLoading)
  const summaryPollingEnabled = useAppSelector(selectDocumentSummaryPollingEnabled)
  const document = useAppSelector(selectDocument)

  const [editedSummary, setEditedSummary] = useState<string | null>(null)

  const documentSummaryContentRef = useContext(DocumentSummaryContentRefContext)

  const [triggerInteractWithAiAgent] = useInteractWithAiAgentMutation()

  const _handleDocumentSummaryEditInteraction = useCallback(async () => {
    if (!aiAgentId) {
      return
    }
    dispatch(setDocumentSummaryInteractionLoading(true))
    try {
      const { data: aiAgentResult } = await triggerInteractWithAiAgent({ accountId, aiAgentId, payload: { type: 'edit', summary: editedSummary } })
      if (!aiAgentResult) {
        return
      }
      const { newAiAgentId, result } = aiAgentResult as DocumentSummaryInteractionResultType
      if (newAiAgentId) {
        dispatch(setDocumentSummaryAiAgentId(newAiAgentId))
      }
      const { message, summary } = result
      if (message) {
        dispatch(addMessage({ type: 'agent', message }))
      }
      if (summary) {
        dispatch(setDocumentSummaryResult({ summary }))
      }
      return aiAgentResult
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setDocumentSummaryInteractionLoading(false))
    }
  }, [accountId, aiAgentId, dispatch, editedSummary, triggerInteractWithAiAgent])

  const handleDocumentSummaryEditInteraction = tracked(_handleDocumentSummaryEditInteraction, {
    eventName: TrackEvent.DOCUMENT_SUMMARY_INTERACTED_WITH,
    eventDetails: { source: 'edit', documentId: document?.id },
  })

  const handleSave = useCallback(async () => {
    const interactionResult = await handleDocumentSummaryEditInteraction()
    if (interactionResult) {
      dispatch(setDocumentSummaryEditing(false))
      setEditedSummary(null)
    }
  }, [dispatch, handleDocumentSummaryEditInteraction])

  const handleEdit = () => {
    const { summary = '' } = documentSummaryResult ?? {}
    setEditedSummary(summary)
    dispatch(setDocumentSummaryEditing(true))
  }

  const handleCancel = () => {
    dispatch(setDocumentSummaryEditing(false))
    setEditedSummary(null)
  }

  const handleSummaryChange = (editorState: EditorState) => {
    setEditedSummary(getEditorHtmlContent(editorState))
  }

  if (summaryLoading || summaryPollingEnabled || !documentSummaryResult) {
    return <div className='skeleton w-100 document-summary-editor-skeleton' />
  }

  return (
    <div className='d-flex flex-column p-4 gap-4 zocks-border-0.5 border zocks-border-color-gray-300'>
      <DocumentSummaryEditorHeader handleEdit={handleEdit} onSave={handleSave} onCancel={handleCancel} editedSummary={editedSummary} />
      <div ref={documentSummaryContentRef}>
        <DocumentSummaryEditorBody onChange={handleSummaryChange} />
      </div>
    </div>
  )
}

interface DocumentSummaryState {
  editorState: EditorState
  displayId: string
}

interface DocumentSummaryEditorBodyProps {
  onChange: (editorState: EditorState) => void
}

const DocumentSummaryEditorBody: React.FC<DocumentSummaryEditorBodyProps> = ({ onChange }) => {
  const documentSummaryResult = useAppSelector(selectDocumentSummaryResult)
  const interactionIsLoading = useAppSelector(selectDocumentSummaryInteractionLoading)
  const isEditing = useAppSelector(selectDocumentSummaryEditing)

  const documentSummaryState = useMemo<DocumentSummaryState>(() => {
    const { summary = '' } = documentSummaryResult ?? {}
    return {
      displayId: nanoid(),
      editorState: getEditorStateFromHtml(summary),
    }
  }, [documentSummaryResult])

  if (isEditing) {
    return (
      <WritableLexicalEditor
        key={documentSummaryState.displayId}
        onChange={onChange}
        initialEditorState={documentSummaryState.editorState}
        className='p-0 border-0'
        lexicalNodes={LEXICAL_NODES}
        header={<RichTextToolbarPlugin className='pb-4 gap-2' disabled={interactionIsLoading} />}
      >
        <EditablePlugin isEditable={!interactionIsLoading} />
      </WritableLexicalEditor>
    )
  }

  return <ReadOnlyLexicalEditor key={documentSummaryState.displayId} initialEditorState={documentSummaryState.editorState} lexicalNodes={LEXICAL_NODES} />
}

interface DocumentSummaryEditorHeaderProps {
  handleEdit: () => void
  onSave: () => void
  onCancel: () => void
  editedSummary: string | null
}

const DocumentSummaryEditorHeader: React.FC<DocumentSummaryEditorHeaderProps> = ({ handleEdit, onSave, onCancel, editedSummary }) => {
  const dispatch = useAppDispatch()

  const interactionLoading = useAppSelector(selectDocumentSummaryInteractionLoading)
  const executionLoading = useAppSelector(selectDocumentSummaryExecutionLoading)
  const isEditing = useAppSelector(selectDocumentSummaryEditing)
  const fileInfo = useAppSelector(selectDocumentFileInfo)

  const { handleCopy, handleRerun } = useDocumentSummary()
  const { aiAgentResult } = useDocumentSummaryAiAgentResult()

  const { documentSummaryExportSupportedIntegrations, documentUploadSupportedIntegrations } = useCurrentUserIntegrations()

  const [lastUsedServiceId, recordLastUsedServiceId] = useLastUsedDocumentConnector()

  const documentConnectorItems: DocumentConnectorActionItem[] = documentUploadSupportedIntegrations.map(({ serviceId }) => ({
    key: serviceId,
    serviceId,
    label: `Save to ${getIntegrationDisplayName(serviceId)}`,
  }))
  const primaryDocumentConnectorKey = documentConnectorItems.find((item) => item.serviceId === lastUsedServiceId)?.key

  const handleSaveToDocumentConnector = (key: string) => {
    recordLastUsedServiceId(key as IntegrationServiceId)
    dispatch(openModal({ type: 'saveDocumentSummaryToDocumentConnector', serviceId: key as IntegrationServiceId }))
  }

  const handleOptionSelected = (selectedOption: SelectOption) => {
    const { id: optionId } = selectedOption

    switch (optionId) {
      case 'edit':
        handleEdit()
        break
      case 'rerun':
        handleRerun()
        break
    }
  }

  const handleOpenIntegrationExportModal = (serviceId: IntegrationServiceId) => {
    dispatch(openModal({ type: 'exportDocumentSummary', serviceId }))
  }

  return (
    <>
      <div className='d-flex align-items-center justify-content-between'>
        <h5>Summary</h5>
        <div className='d-flex align-items-center gap-2'>
          {documentSummaryExportSupportedIntegrations.map(({ serviceId }) => (
            <IntegrationExportButton
              key={serviceId}
              serviceId={serviceId}
              className='px-2 zocks-py-0.25'
              onClick={() => handleOpenIntegrationExportModal(serviceId)}
              showTitle={true}
            />
          ))}
          {documentConnectorItems.length ? (
            <DocumentConnectorActionButton
              items={documentConnectorItems}
              onSelect={handleSaveToDocumentConnector}
              primaryKey={primaryDocumentConnectorKey}
              disabled={executionLoading || interactionLoading || isEditing || !aiAgentResult}
              buttonClassName='px-2 zocks-py-0.25'
            />
          ) : null}
          {isEditing ? (
            <>
              <Button variant='secondary' className='px-2 zocks-py-0.25' onClick={onCancel} disabled={executionLoading || interactionLoading}>
                Cancel
              </Button>
              <Button
                variant='primary'
                className='px-2 zocks-py-0.25'
                onClick={onSave}
                disabled={!editedSummary}
                loading={executionLoading || interactionLoading}
              >
                Save
              </Button>
            </>
          ) : (
            <Button variant='header' className='zocks-gap-0.5 px-2 zocks-py-0.25' onClick={handleCopy} disabled={executionLoading || interactionLoading}>
              <i className='bi bi-copy' />
              Copy
            </Button>
          )}
          <div className='document-summary-editor-header-dropdown'>
            <SelectDropdown
              className='border-0 p-2'
              options={getEditorHeaderDropdownOptions(isEditing, !fileInfo).map(({ id, label, disabled }) => ({ id, label, disabled }))}
              icon='bi-three-dots-vertical'
              iconOnly
              hideArrows
              DropDownItemOptionComponent={DocumentSummaryEditorHeaderDropdownItemOptionComponent}
              onOptionSelected={handleOptionSelected}
              disabled={executionLoading || interactionLoading}
            />
          </div>
        </div>
      </div>
    </>
  )
}

const DocumentSummaryEditorHeaderDropdownItemOptionComponent: React.FC<DropDownItemOptionComponentProps> = ({ optionId, optionLabel, disabled, onClick }) => {
  const option = getEditorHeaderDropdownOptions().find((e) => e.id === optionId)

  if (!option) {
    return null
  }

  const { icon } = option

  return (
    <CustomDropdownItemOption role='button' onClick={onClick} className={classNames('d-flex gap-2 align-items-center', { 'dropdown-item-disabled': disabled })}>
      <i className={`bi ${icon}`} />
      {optionLabel}
    </CustomDropdownItemOption>
  )
}
